import React, { memo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ChartsAreaIntensity = memo(({ stats, loading, scoreType, scoreTypeDisplayName }) => {
    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-2">Updating intensity charts...</p>
                </div>
            </div>
        );
    }

    if (!stats) return null;

    // Custom tooltip formatter
    const customTooltipFormatter = (value, name) => {
        const nameMap = {
            'green': '80-100% (Excellent)',
            'yellow': '40-80% (Good)', 
            'red': '0-40% (Needs Practice)',
            'average': `${scoreTypeDisplayName} Score`,
            'intensity': 'Practice Time (min)'
        };
        return [value, nameMap[name] || name];
    };

    return (
        <div className="space-y-8">
            {/* Main Charts Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Left Column - Distribution Charts */}
                <div className="space-y-6">
                    {/* Score Distribution */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            📈 {scoreTypeDisplayName} Distribution Over Time
                        </h3>
                        <div className="h-64 sm:h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stats['data']}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                    <YAxis 
                                        tick={{ fontSize: 12 }}
                                        label={{ 
                                            value: 'Number of Sentences', 
                                            angle: -90, 
                                            position: 'insideLeft',
                                            style: { fontSize: 12 }
                                        }}
                                    />
                                    <Tooltip formatter={customTooltipFormatter} />
                                    <Legend 
                                        wrapperStyle={{ fontSize: '12px' }} 
                                        formatter={(value) => customTooltipFormatter(null, value)[1]}
                                    />
                                    <Bar name="green" dataKey="green" stackId="a" fill="#10B981" />
                                    <Bar name="yellow" dataKey="yellow" stackId="a" fill="#F59E0B" />
                                    <Bar name="red" dataKey="red" stackId="a" fill="#EF4444" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Normalized Distribution */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            📊 {scoreTypeDisplayName} Normalized Distribution (%)
                        </h3>
                        <div className="h-64 sm:h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stats['normdata']}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                    <YAxis 
                                        tick={{ fontSize: 12 }}
                                        label={{ 
                                            value: 'Percentage (%)', 
                                            angle: -90, 
                                            position: 'insideLeft',
                                            style: { fontSize: 12 }
                                        }}
                                    />
                                    <Tooltip formatter={customTooltipFormatter} />
                                    <Legend 
                                        wrapperStyle={{ fontSize: '12px' }} 
                                        formatter={(value) => customTooltipFormatter(null, value)[1]}
                                    />
                                    <Bar name="green" dataKey="green" stackId="a" fill="#10B981" />
                                    <Bar name="yellow" dataKey="yellow" stackId="a" fill="#F59E0B" />
                                    <Bar name="red" dataKey="red" stackId="a" fill="#EF4444" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Right Column - Performance & Intensity Charts */}
                <div className="space-y-6">
                    {/* Practice Intensity */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">
                                ⏱️ Practice Intensity
                            </h3>
                            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                Total: {stats['intensity_total']} min
                            </div>
                        </div>
                        <div className="h-48 sm:h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stats['intensity']}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                    <YAxis 
                                        tick={{ fontSize: 12 }}
                                        label={{ 
                                            value: 'Minutes', 
                                            angle: -90, 
                                            position: 'insideLeft',
                                            style: { fontSize: 12 }
                                        }}
                                    />
                                    <Tooltip formatter={customTooltipFormatter} />
                                    <Bar name="intensity" dataKey="intensity" fill="#3B82F6" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Performance Averages */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            🎯 {scoreTypeDisplayName} Performance Averages
                        </h3>
                        
                        {/* All Attempts */}
                        <div className="mb-6">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">All Attempts</h4>
                            <div className="h-40">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stats['average']}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                        <YAxis 
                                            domain={[70, 100]} 
                                            tick={{ fontSize: 10 }}
                                            label={{ 
                                                value: scoreTypeDisplayName, 
                                                angle: -90, 
                                                position: 'insideLeft',
                                                style: { fontSize: 10 }
                                            }}
                                        />
                                        <Tooltip formatter={(value) => [value, `${scoreTypeDisplayName} Score`]} />
                                        <Bar name="average" dataKey="average" fill="#10B981" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* First Attempts */}
                        <div className="mb-6">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">First Attempts</h4>
                            <div className="h-40">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stats['first']}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                        <YAxis 
                                            domain={[70, 100]} 
                                            tick={{ fontSize: 10 }}
                                            label={{ 
                                                value: scoreTypeDisplayName, 
                                                angle: -90, 
                                                position: 'insideLeft',
                                                style: { fontSize: 10 }
                                            }}
                                        />
                                        <Tooltip formatter={(value) => [value, `${scoreTypeDisplayName} Score`]} />
                                        <Bar name="average" dataKey="average" fill="#10B981" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Best Attempts */}
                        <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Best Attempts</h4>
                            <div className="h-40">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={stats['best']}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                        <YAxis 
                                            domain={[70, 100]} 
                                            tick={{ fontSize: 10 }}
                                            label={{ 
                                                value: scoreTypeDisplayName, 
                                                angle: -90, 
                                                position: 'insideLeft',
                                                style: { fontSize: 10 }
                                            }}
                                        />
                                        <Tooltip formatter={(value) => [value, `${scoreTypeDisplayName} Score`]} />
                                        <Bar name="average" dataKey="average" fill="#10B981" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold">{stats.data?.length || 0}</div>
                    <div className="text-blue-100 text-sm">Time Periods</div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold">
                        {stats.data?.reduce((sum, item) => sum + (item.green || 0), 0) || 0}
                    </div>
                    <div className="text-green-100 text-sm">Excellent Scores</div>
                </div>
                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold">
                        {stats.data?.reduce((sum, item) => sum + (item.yellow || 0), 0) || 0}
                    </div>
                    <div className="text-yellow-100 text-sm">Good Scores</div>
                </div>
                <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold">
                        {stats.data?.reduce((sum, item) => sum + (item.red || 0), 0) || 0}
                    </div>
                    <div className="text-red-100 text-sm">Needs Practice</div>
                </div>
            </div>

            {/* Performance Averages Legend */}
            <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                <h4 className="text-sm font-semibold text-green-800 mb-2">
                    {scoreTypeDisplayName} Performance Averages Legend
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-green-200">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <div>
                            <div className="font-medium text-gray-700">All Attempts</div>
                            <div className="text-gray-500">Average of all practice sessions</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-green-200">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <div>
                            <div className="font-medium text-gray-700">First Attempts</div>
                            <div className="text-gray-500">Average of initial attempts only</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-green-200">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <div>
                            <div className="font-medium text-gray-700">Best Attempts</div>
                            <div className="text-gray-500">Average of highest scores achieved</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

ChartsAreaIntensity.displayName = 'ChartsAreaIntensity';

export default ChartsAreaIntensity;